// Availability decisions for the feature matrix, kept pure and out of the
// component so they can be exercised directly. The grid states "the flag does
// not exist in this org" for a cell it renders as "—", which is a claim about
// a tenant database; every path that cannot support that claim must land on
// "unknown" instead.

/**
 * True when we have no flag state for this organization.
 *
 * Two distinct ways that happens:
 *  - the backend read the org and failed (`unavailableOrgs`), or
 *  - the matrix response never covered the org at all (`reportedOrgs`).
 *
 * The second is reachable because /organizations and /matrix are separate
 * snapshots: /matrix can answer from matrix_cache, which creating an
 * organization does not invalidate, so the org list can contain an org the
 * matrix never saw.
 *
 * @param {number} orgId
 * @param {Set<number>} unavailableOrgs
 * @param {Set<number>|null} reportedOrgs  null when the backend sent no
 *   availability block at all, where this check cannot apply.
 */
export function isOrgUnknown(orgId, unavailableOrgs, reportedOrgs) {
  if (unavailableOrgs && unavailableOrgs.has(orgId)) return true;
  if (reportedOrgs !== null && reportedOrgs !== undefined) {
    return !reportedOrgs.has(orgId);
  }
  return false;
}

/** Cell key. Single source of truth so the grid and these helpers agree. */
export function keyFor(orgId, feature) {
  return `${orgId}::${feature}`;
}

/**
 * True when any cell currently shown for this org is in the unknown state.
 *
 * The org-level signals are not enough on their own. A refresh reports on one
 * cell but replaces the judgment for the whole organization, so a later
 * successful save can clear the org from `unavailableOrgs` while an earlier
 * cell is still "?" — and an existing cell takes precedence over the org
 * fallback, so clearing the set does not resolve it. Counting the displayed
 * cells keeps the warning honest about what is actually on screen.
 *
 * Marking that earlier cell available instead would be worse: it would turn a
 * failed read into a claim that the flag is absent.
 */
export function orgHasUnknownCell(orgId, features, cells) {
  for (const feature of features) {
    const cell = cells.get(keyFor(orgId, feature));
    if (cell && cell.available === false) return true;
  }
  return false;
}

/**
 * The availability set after a single-org refresh.
 *
 * A filtered refresh only tells us about `orgId`, so the rest of the set is
 * left untouched. Returns the same Set instance when nothing changed, so a
 * React state setter does not re-render for free.
 */
export function mergeRefreshedAvailability(prev, orgId, available) {
  const hasIt = prev.has(orgId);
  if (available === !hasIt) return prev;
  const next = new Set(prev);
  if (available) next.delete(orgId);
  else next.add(orgId);
  return next;
}
